import { AxiosError } from 'axios';
import React, { useContext } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';

import { MarketId } from 'types/market-id.enum';
import { getDispatchDates } from 'views/order-feed/api/get-dispatch-dates';
import { getOrderNotifications } from 'views/order-feed/api/get-notifications';
import { OrderFeedParams } from 'views/order-feed/types/order-feed-params.type';
import { OrderNotifications } from 'views/order-feed/types/order-notifications.type';
import { DispatchSchedule } from 'views/order-feed/types/shipping-schedule.type';
import { isValidRouteParam } from 'views/order-feed/utils/is-valid-route-param';

type SitesProviderProps = {
  children: React.ReactNode;
};

type State = {
  notificationsQuery: UseQueryResult<OrderNotifications, AxiosError<unknown, any>>;
  dispatchDatesQuery: UseQueryResult<DispatchSchedule, AxiosError<unknown, any>>;
};

const OrderFeedStateContext = React.createContext<State | undefined>(undefined);

const OrderFeedProvider: React.FC<SitesProviderProps> = ({ children }) => {
  const params = useParams<OrderFeedParams>();
  const {
    account: { company },
  } = useAccountState();

  const notificationsQuery = useGet(
    ['getNotifications', company?.customerErpId],
    ({ queryKey }) => getOrderNotifications(String(queryKey[1])),
    {
      enabled:
        company?.marketId === MarketId.Norway &&
        (!!company?.features?.hasOrderFeed || !!company?.features.hasOrderRead) &&
        !!company?.customerErpId,
    }
  );

  const dispatchDatesQuery = useGet(
    ['getDispatchDates', company?.marketId, company?.customerErpId, params.siteErpId],
    ({ queryKey }) => getDispatchDates(Number(queryKey[1]), String(queryKey[2]), String(queryKey[3])),
    {
      enabled:
        !!(company?.features?.hasOrderFeed && company?.marketId && company?.customerErpId) &&
        isValidRouteParam(params.siteErpId),
    }
  );

  return (
    <OrderFeedStateContext.Provider
      value={{
        notificationsQuery,
        dispatchDatesQuery,
      }}>
      {children}
    </OrderFeedStateContext.Provider>
  );
};

function useOrderFeedState() {
  const context = useContext(OrderFeedStateContext);
  if (context === undefined) {
    throw new Error('useOrderFeedState must be used within an OrderFeedProvider');
  }
  return context;
}

export { OrderFeedProvider, useOrderFeedState };
